import { useCallback } from 'react';
import dayjs from 'dayjs';
import { ITimer } from '../interface/timer';
import { LOCAL_STORAGE_NAMES, ROUTES } from '../constants/constants';
import { computeInitialDuration, decimalHours } from '../constants/utilityFunctions';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import { useAnalytics } from './useAnalytics';
import { ITransaction } from '../interface/transaction';
import { useTransaction } from '../context/TransactionContext';
import { useNavigationStack } from '../context/NavigationStackContext';
import { showToast } from '../components/utils/toast';
import { useTranslation } from 'react-i18next';

const notifyTimersUpdated = () => {
  // MainLayout does not get the updated timers from context, so we need to notify it to re-render and get the latest timers from localStorage
  globalThis.dispatchEvent(new CustomEvent('timersUpdated', { detail: Math.random().toString() }));
};

interface UseTimerActionsParams {
  timer: ITimer | undefined;
  timers: ITimer[];
  intervalStart: string | null;
  setIsRunning: (v: boolean) => void;
  setIntervalStart: (v: string | null) => void;
  setSeconds: (v: number) => void;
  refetch?: () => void;
}

export const useTimerActions = ({
  timer,
  timers,
  intervalStart,
  setIsRunning,
  setIntervalStart,
  setSeconds,
  refetch,
}: UseTimerActionsParams) => {
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();
  const { updateTransaction } = useTransaction();
  const { push } = useNavigationStack();

  // Toggle Timer
  const toggleTimer = (overrideTimer?: ITimer) => {
    const timerToUse = overrideTimer ?? timer;
    if (!timerToUse) return;
    const isRunning = timerToUse.isRunning;
    const newRunningState = !isRunning;
    setIsRunning(newRunningState);

    const now = new Date().toISOString();
    let updatedTimer = {
      ...timerToUse,
      isRunning: newRunningState,
      modifiedOn: now,
    } as ITimer;

    if (newRunningState) {
      // START
      const startTime = new Date().toISOString();
      setIntervalStart(startTime);
      updatedTimer = {
        ...updatedTimer,
        intervalStart: startTime,
      };
    } else {
      // STOP
      const intervalStartTime = intervalStart ?? timerToUse?.intervalStart;
      const intervalEnd = now;

      if (intervalStartTime) {
        const intervalDuration = Math.floor(
          (new Date(intervalEnd).getTime() - new Date(intervalStartTime).getTime()) / 1000,
        );

        updatedTimer = {
          ...updatedTimer,
          duration: (timerToUse?.duration ?? 0) + intervalDuration,
          intervalStart: undefined,
          intervals: [
            ...(timerToUse?.intervals ?? []),
            {
              start: intervalStartTime,
              end: intervalEnd,
              duration: intervalDuration,
            },
          ],
        };
        setIntervalStart(null);
      }
    }

    // Turn off other timers safely
    const current = new Date().toISOString();
    const otherTimers = timers
      .filter((t) => t.id !== timerToUse?.id)
      .map((t) => {
        if (t.isRunning && t.intervalStart) {
          const intervalDuration = Math.floor(
            (new Date(current).getTime() - new Date(t.intervalStart).getTime()) / 1000,
          );
          const value = computeInitialDuration(t.id ?? '');

          return {
            ...t,
            isRunning: false,
            modifiedOn: current,
            intervalStart: undefined,
            duration: value,
            intervals: [
              ...(t.intervals ?? []),
              {
                start: t.intervalStart,
                end: current,
                duration: intervalDuration,
              },
            ],
          };
        } else {
          return { ...t, isRunning: false };
        }
      });

    const timersToUpdate = [...otherTimers, updatedTimer].sort(
      (a, b) => new Date(b.timerDate ?? '').getTime() - new Date(a.timerDate ?? '').getTime(),
    );

    localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(timersToUpdate));
    notifyTimersUpdated();
    refetch?.();
  };

  // Reset Timer
  const handleReset = useCallback(() => {
    setIsRunning(false);
    setSeconds(0);

    const updatedTimer = {
      ...timer,
      isRunning: false,
      duration: 0,
      modifiedOn: new Date().toISOString(),
    };

    const timersArr: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
    const updatedTimers = timersArr.map((t) => (t.id === updatedTimer.id ? updatedTimer : t));
    localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
    notifyTimersUpdated();
    refetch?.();
    showToast('success', t('timers.timerReset'));
  }, [timer, setIsRunning, setSeconds]);

  // Add 5 Minutes
  const handleAddFiveMinutes = useCallback(() => {
    const timersArr: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
    const secondsNow = computeInitialDuration(timer?.id ?? '') ?? 0;
    const newDuration = secondsNow + 300;

    // If running, shift intervalStart backwards
    let newIntervalStart = intervalStart;
    if (timer?.isRunning && intervalStart) {
      const current = new Date(intervalStart);
      newIntervalStart = new Date(current.getTime() - 300 * 1000).toISOString();
    }

    const updatedTimer = {
      ...timer,
      modifiedOn: new Date().toISOString(),
      duration: timer?.isRunning ? (timer?.duration ?? 0) : newDuration,
      intervalStart: timer?.isRunning ? newIntervalStart : undefined,
    };

    setSeconds(newDuration);
    setIntervalStart(newIntervalStart ?? null);

    const updatedTimers = timersArr.map((t) => (t.id === updatedTimer.id ? updatedTimer : t));
    localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
    notifyTimersUpdated();
    showToast('success', t('timers.fiveMinAdded'));
  }, [timer, intervalStart, setIntervalStart, setSeconds]);

  // Remove 5 Minutes
  const handleRemoveFiveMinutes = useCallback(() => {
    const timersArr: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
    const baseDuration = timer?.duration ?? 0;

    // Calculate current elapsed time if running
    let elapsedSinceStart = 0;
    if (timer?.isRunning && intervalStart) {
      const start = new Date(intervalStart).getTime();
      elapsedSinceStart = Math.floor((Date.now() - start) / 1000);
    }

    const secondsNow = computeInitialDuration(timer?.id ?? '') ?? 0;
    const totalDuration = timer?.isRunning ? baseDuration + elapsedSinceStart : secondsNow;
    const newDuration = Math.max(0, totalDuration - 300);

    let updatedDuration = newDuration;
    let newIntervalStart = intervalStart;

    if (timer?.isRunning && intervalStart) {
      const adjustment = totalDuration - newDuration;
      const current = new Date(intervalStart);
      newIntervalStart = new Date(current.getTime() + adjustment * 1000).toISOString();
      updatedDuration = baseDuration;
    }

    const updatedTimer = {
      ...timer,
      modifiedOn: new Date().toISOString(),
      duration: updatedDuration,
      intervalStart: timer?.isRunning ? newIntervalStart : undefined,
    };

    setSeconds(newDuration);
    setIntervalStart(newIntervalStart ?? null);

    const updatedTimers = timersArr.map((t) => (t.id === updatedTimer.id ? updatedTimer : t));
    localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
    notifyTimersUpdated();
    showToast('success', t('timers.fiveMinSubtracted'));
  }, [timer, intervalStart, setIntervalStart, setSeconds]);

  // Delete Timer
  const handleRemoveTimer = useCallback(() => {
    const timersArr: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
    const updatedTimers = timersArr.filter((t) => t.id !== timer?.id);
    localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
    notifyTimersUpdated();
    trackBusinessEvent(AnalyticsFeatureOperation.TIMER_DELETED);
    refetch?.();
  }, [timer, refetch, trackBusinessEvent]);

  // Edit Timer
  const handleEdit = useCallback(() => {
    push(`${ROUTES.TIMER_ADD_EDIT}/${timer?.id}`);
  }, [push, timer?.id]);

  // Timer Detail
  const handleTimerDetail = useCallback(() => {
    push(`${ROUTES.TIMER_DETAIL}/${timer?.id}`);
  }, [push, timer?.id]);

  // Add Transaction
  const handleAddTransaction = useCallback(() => {
    const secondsNow = computeInitialDuration(timer?.id ?? '') ?? 0;
    const billableHours = decimalHours(secondsNow);
    updateTransaction({
      transactionTypeId: timer?.transactionTypeId ?? null,
      transactionType: timer?.transactionType ?? null,
      matterId: timer?.matter?.id ?? null,
      matter: timer?.matter ?? null,
      skillId: timer?.skillId ?? null,
      skill: timer?.skill ?? null,
      subject: timer?.subject ?? '',
      currentSubject: timer?.subject ?? '',
      principalCompany: timer?.matter?.principalCompany ?? null,
      principalPrivatePerson: timer?.matter?.principalPrivatePerson ?? null,
      principalCompanyId: timer?.matter?.principalCompany?.id ?? null,
      principalPrivatePersonId: timer?.matter?.principalPrivatePerson?.id ?? null,
      transactionDate: dayjs(timer?.timerDate).format('YYYY-MM-DD'),
      billableQuantity: billableHours,
      workedHours: billableHours,
      unitPrice: timer?.transactionType?.unitPrice,
      overrideWarnings: true,
      timerId: timer?.id,
      onlyHourBasedTransactionTypes: true,
    } as ITransaction);
    push(ROUTES.ADD_EDIT_TRANSACTION);
  }, [timer, updateTransaction, push]);

  return {
    toggleTimer,
    handleReset,
    handleAddFiveMinutes,
    handleRemoveFiveMinutes,
    handleRemoveTimer,
    handleEdit,
    handleTimerDetail,
    handleAddTransaction,
  };
};
const getCurrentOrganizationId = (): string | undefined => {
  const currentOrganisation = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
  return currentOrganisation ? JSON.parse(currentOrganisation)?.organizationId : undefined;
};

const isRunningForUserAndOrg = (timer: ITimer, userId: string, organizationId: string) =>
  timer.userId === userId && timer.organizationId === organizationId && timer.isRunning;

export const getRunningTimersForCurrentOrg = (userId: string): ITimer[] => {
  const organizationId = getCurrentOrganizationId();
  if (!organizationId) return [];

  const fullTimers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
  return fullTimers.filter((t) => isRunningForUserAndOrg(t, userId, organizationId));
};

export const pauseRunningTimer = (timer: ITimer): ITimer => {
  if (!timer.isRunning) return timer;

  const now = new Date().toISOString();
  const pausedTimer: ITimer = {
    ...timer,
    isRunning: false,
    modifiedOn: now,
    intervalStart: undefined,
  };

  if (timer.intervalStart) {
    const intervalDuration = Math.floor(
      (new Date(now).getTime() - new Date(timer.intervalStart).getTime()) / 1000,
    );
    pausedTimer.duration = (timer.duration ?? 0) + intervalDuration;
    pausedTimer.intervals = [
      ...(timer.intervals ?? []),
      { start: timer.intervalStart, end: now, duration: intervalDuration },
    ];
  }

  const fullTimers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
  const updatedTimers = fullTimers.map((t) => (t.id === timer.id ? pausedTimer : t));
  localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
  notifyTimersUpdated();
  return pausedTimer;
};

export const stopAllRunningTimersForCurrentOrg = (userId: string) => {
  const organizationId = getCurrentOrganizationId();
  if (!organizationId) return; // Early return if no current organization

  const fullTimers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
  const runningTimers = fullTimers.filter((t) => isRunningForUserAndOrg(t, userId, organizationId));

  if (runningTimers.length === 0) return; // Early return if no running timers

  const current = new Date().toISOString();
  const stoppedTimers = runningTimers.map((t) => {
    if (t.intervalStart) {
      const intervalDuration = Math.floor(
        (new Date(current).getTime() - new Date(t.intervalStart).getTime()) / 1000,
      );
      const computedDuration = computeInitialDuration(t.id ?? '');
      return {
        ...t,
        isRunning: false,
        modifiedOn: current,
        intervalStart: undefined,
        duration: computedDuration,
        intervals: [
          ...(t.intervals ?? []),
          {
            start: t.intervalStart,
            end: current,
            duration: intervalDuration,
          },
        ],
        organizationId,
      };
    } else {
      return { ...t, isRunning: false, organizationId };
    }
  });

  const otherFullTimers = fullTimers.filter(
    (t) => !isRunningForUserAndOrg(t, userId, organizationId),
  );
  const updatedFullTimers = [...otherFullTimers, ...stoppedTimers];
  localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedFullTimers));
  notifyTimersUpdated();
};
